<template>
    <div class="simple-modal" v-show="visible" :aria-hidden="!visible">
        <div class="simple-modal__backdrop" @click="onBackdropClick"></div>
        <div class="simple-modal__dialog" :style="dialogStyle">
            <div class="simple-modal__header" v-if="showHeader">
                <h5 class="simple-modal__title" v-if="title">{{ title }}</h5>
                <button type="button" class="simple-modal__close" @click="close" aria-label="Close">&times;</button>
            </div>
            <div class="simple-modal__body" ref="bodyRef">
                <slot />
            </div>
            <div class="simple-modal__footer" v-if="$slots.footer">
                <slot name="footer" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { defineExpose, defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    width: { type: [String, Number], default: 520 },
    closeOnBackdrop: { type: Boolean, default: true },
    showHeader: { type: Boolean, default: true }
});
const emit = defineEmits(['update:modelValue', 'open', 'close', 'body-scroll']);

const visible = ref(!!props.modelValue);
watch(() => props.modelValue, (val) => { visible.value = val; if (val) emit('open'); });
watch(visible, (val) => emit('update:modelValue', val));

function open() { visible.value = true; }
function close() { visible.value = false; emit('close'); }
function onBackdropClick() { if (props.closeOnBackdrop) close(); }

const dialogStyle = computed(() => {
    let w = props.width;
    if (typeof w === 'number') w = w + 'px';
    return { maxWidth: w };
});

// Scroll handling
const bodyRef = ref(null);
let scrollTimer = null;
const SCROLL_DEBOUNCE_MS = 300; // adjust as needed
function immediateScrollEmit() {
    if (!bodyRef.value) return;
    emit('body-scroll', { x: bodyRef.value.scrollLeft, y: bodyRef.value.scrollTop });
}
function emitScroll() {
    if (!bodyRef.value) return;
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
        immediateScrollEmit();
        scrollTimer = null;
    }, SCROLL_DEBOUNCE_MS);
}
function getScrollPosition() {
    if (!bodyRef.value) return { x: 0, y: 0 };
    return { x: bodyRef.value.scrollLeft, y: bodyRef.value.scrollTop };
}
function scrollTo(x, y, options = {}) {
    if (!bodyRef.value) return;
    const behavior = options.behavior || 'auto';
    bodyRef.value.scrollTo({ left: x, top: y, behavior });
    // Emit after scroll (in next tick to ensure updated values)
    nextTick(() => immediateScrollEmit());
}
function scrollBy(dx, dy, options = {}) {
    if (!bodyRef.value) return;
    const behavior = options.behavior || 'auto';
    bodyRef.value.scrollBy({ left: dx, top: dy, behavior });
    nextTick(() => immediateScrollEmit());
}
onMounted(() => {
    if (bodyRef.value) {
        bodyRef.value.addEventListener('scroll', emitScroll, { passive: true });
    }
});
onUnmounted(() => {
    if (bodyRef.value) {
        bodyRef.value.removeEventListener('scroll', emitScroll);
    }
});

defineExpose({ open, close, getScrollPosition, scrollTo, scrollBy });
</script>

<style scoped>
.simple-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: none;
    z-index: 1050;
}

.simple-modal[aria-hidden="true"] {
    display: none;
}

.simple-modal[aria-hidden="false"],
.simple-modal[v-show="true"] {
    display: block;
}

.simple-modal__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
}

.simple-modal__dialog {
    position: relative;
    background: #fff;
    width: 100%;
    margin: 60px auto;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 0;
    display: flex;
    flex-direction: column;
}

.simple-modal__header {
    padding: 12px 16px;
    border-bottom: 1px solid #ddd;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.simple-modal__title {
    margin: 0;
    font-size: 16px;
}

.simple-modal__close {
    background: none;
    border: none;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
}

.simple-modal__body {
    padding: 16px;
    max-height: 70vh;
    overflow-y: auto;
}

.simple-modal__footer {
    padding: 12px 16px;
    border-top: 1px solid #ddd;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
